(function() {
  /**
   * Debounces a function. Only calls it once it has been quiet for <delay> milliseconds
   * @param callback Function to call
   * @param delay How long the debounced function needs to go without being called, before trigger the callback
   */
  function debounce(callback, delay = 250) {
    let timeout = undefined;
    return function() {
      const context = this;
      const args = arguments;
      clearTimeout(timeout);

      timeout = setTimeout(() => {
        timeout = undefined;
        callback.apply(context, args);
      }, delay);
    };
  }

  class MegaMenu {
    static VIEW_ACTIVATION_TIMEOUT_MS = 250;

    constructor(menu_element) {
      this.id = menu_element.dataset.menuId;
      this.root_element = menu_element;
      this.is_active = this.root_element.classList.contains('is-active');

      this._min_window_size = 721;
      this._can_be_enabled = false;

      if (this.root_element.dataset.minWindowSize) {
        const temp = parseInt(this.root_element.dataset.minWindowSize, 10);
        if (!isNaN(temp)) {
          this._min_window_size = temp;
        }
      }

      this.view_activation_timeout = null;
      this.deactivation_timeout = null;
      this.active_view_id = null;
      this.pending_view_id = null;
      this.anchor_top_position = 0;

      this.view_elements = menu_element.querySelectorAll('.js-mega-menu__view');
      this.opener_elements = document.querySelectorAll(`[data-opener-for-menu-id="${this.id}"]`);
      this.closer_hovers = document.querySelectorAll(`[data-closer-for-menu-id="${this.id}"]`);
      this.anchor_element = document.querySelector(`[data-anchor-for-menu-id="${this.id}"]`);
      this.result_links = menu_element.querySelectorAll('.js-mega-menu-result-link');
      this.background = menu_element.querySelector('.js-mega-menu__bg');
      this.blackout = menu_element.querySelector('.js-mega-menu__blackout');

      // Buttons that instantly close this mega menu with no delay
      this.instant_closers = document.querySelectorAll(`[data-instant-closer-for-menu-id="${this.id}"]`);

      // Any result links that are clicked make the menu deactivate
      for (let i = 0; i < this.result_links.length; i++) {
        this.result_links[i].addEventListener('click', () => this.deactivate());
      }

      this.views = [];
      for (let i = 0; i < this.view_elements.length; i++) {
        this.views.push(new MegaMenuView(this.view_elements[i]));
      }

      this.blackout_hover_handler = (e) => this.handleBlackoutHover(e);
      this.background_hover_handler = (e) => this.handleBackgroundHover(e);

      this.addListenersToOpenerElements();
      this.addListenersToCloserHoverElements();
      this.addListenersToInstantClosers();
      this.addEscapeListener();

      // Run once to correctly set variables
      this.handleWindowResize();
      window.addEventListener('resize', debounce(() => this.handleWindowResize(), 200));
    }

    activate() {
      this.stopDeactivation();
      if (!this._can_be_enabled) {
        return;
      }
      if (this.is_active) {
        return;
      }

      document.documentElement.classList.add('js-no-scroll');
      this.is_active = true;
      // Place it on the screen correctly
      this.positionMegaMenu();

      this.root_element.classList.add('is-active');
      this.blackout.addEventListener('mouseover', this.blackout_hover_handler);
      this.background.addEventListener('mouseover', this.background_hover_handler);
    }
    deactivate() {
      if (!this.is_active) {
        return;
      }

      document.documentElement.classList.remove('js-no-scroll');

      this.is_active = false;
      this.root_element.classList.remove('is-active');
      this.blackout.removeEventListener('mouseover', this.blackout_hover_handler);
      this.background.removeEventListener('mouseover', this.background_hover_handler);

      this.activateProperView(null);
    }

    addListenersToOpenerElements() {
      for (let i = 0; i < this.opener_elements.length; i++) {
        const element = this.opener_elements[i];
        element.addEventListener('mouseover', (e) => {
          // Stop conflict of nested opener/closer elements. First come first serve
          e.stopPropagation();

          // Even if no menu is showing, wait at least a little to avoid a unintended view of the menu
          this.queueActivationOfProperView(element.dataset.forViewId);
        });
        element.addEventListener('mouseout', () => {
          // If we are no longer hovering over this opener link, start deactivating the mega menu
          // The mega menu will stay activated so long as another activation occurs before the timeout is reached
          this.queueDeactivation();

          // Also, stop the queued activation of this view id
          this.stopQueuedActivationOfViewId(element.dataset.forViewId);
        });

        element.addEventListener('click', () => {
          // No queue needed. A click has no flicker problems
          this.activateProperView(element.dataset.forViewId);
        });
      }
    }
    addListenersToCloserHoverElements() {
      for (let i = 0; i < this.closer_hovers.length; i++) {
        const element = this.closer_hovers[i];
        element.addEventListener('mouseover', (e) => {
          // Stop conflict of nested opener/closer elements. First come first serve
          e.stopPropagation();
          this.queueDeactivation();
        });
      }
    }

    addListenersToInstantClosers() {
      for (let i = 0; i < this.instant_closers.length; i++) {
        const element = this.instant_closers[i];
        element.addEventListener('click', (e) => {
          this.deactivate();
        });
      }
    }

    addEscapeListener() {
      window.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.deactivate();
        }
      });
    }

    // Prevent flickering when changing views by issuing a short timeout
    queueActivationOfProperView(view_id) {
      // A new queued activation means we want to keep the mega menu active
      this.stopDeactivation();

      // The active view is correct. Cancel any queued view activation
      if (this.active_view_id === view_id) {
        this.clearViewActivationTimeout();
        return;
      }

      if (this.pending_view_id === view_id) {
        return;
      }

      this.pending_view_id = view_id;
      this.view_activation_timeout = setTimeout(() => {
        this.activateProperView(view_id);
      }, MegaMenu.VIEW_ACTIVATION_TIMEOUT_MS);
    }
    stopQueuedActivationOfViewId(view_id) {
      if (this.pending_view_id !== view_id) {
        return;
      }

      this.clearViewActivationTimeout();
    }
    clearViewActivationTimeout() {
      clearTimeout(this.view_activation_timeout);
      this.view_activation_timeout = null;
      this.pending_view_id = null;
    }

    activateProperView(view_id) {
      // Remove any pending changes because we activated directly
      this.clearViewActivationTimeout();

      // We use a view_id of null to deactivate all views
      if (view_id) {
        this.activate();
      }

      if (this.active_view_id === view_id) {
        return;
      }

      this.active_view_id = view_id;
      for (let i = 0; i < this.views.length; i++) {
        const view = this.views[i];
        if (view.id !== view_id) {
          view.deactivate();
          continue;
        }

        view.activate();
      }
    }

    queueDeactivation() {
      if (this.deactivation_timeout) {
        return;
      }

      this.deactivation_timeout = setTimeout(() => this.deactivate(), 300);
    }
    stopDeactivation() {
      if (!this.deactivation_timeout) {
        return;
      }

      clearTimeout(this.deactivation_timeout);
      this.deactivation_timeout = null;
    }

    handleBlackoutHover(e) {
      this.queueDeactivation();
    }
    handleBackgroundHover() {
      this.stopDeactivation();
    }

    positionMegaMenu() {
      if (!this.anchor_element) {
        return;
      }

      const rect = this.anchor_element.getBoundingClientRect();
      const top = rect.top + rect.height;

      if (top !== this.anchor_top_position) {
        this.root_element.style.top = `${top}px`;
        this.anchor_top_position = top;
      }
    }

    handleWindowResize() {
      this.positionMegaMenu();

      if (window.innerWidth < this._min_window_size) {
        this._can_be_enabled = false;
        this.deactivate();
        return;
      }

      this._can_be_enabled = true;
    }
  }

  class MegaMenuView {
    static HOVER_CATEGORY_TIMEOUT_MS = 250;

    constructor(view_element) {
      this.id = view_element.dataset.viewId;
      this.category_hover_timeout = null;
      this.hovering_over_category_id = null;
      this.root_element = view_element;

      this.categories = view_element.querySelectorAll('.js-mega-menu__category');
      this.menus = view_element.querySelectorAll('.js-mega-menu__category-menu');

      this.category_ids = [];
      for (let i = 0; i < this.categories.length; i++) {
        this.category_ids.push(this.categories[i].dataset.forCategoryId);
      }
    }

    activate() {
      this.root_element.classList.add('is-active');
      this.addListenersToCategoryLinks();

      // Activate the first category by default
      this.activateMenu(this.category_ids[0]);
    }
    deactivate() {
      this.root_element.classList.remove('is-active');
      this.removeListenersToCategoryLinks();
      this.activateMenu(null);
    }

    addListenersToCategoryLinks() {
      for (let i = 0; i < this.categories.length; i++) {
        const category = this.categories[i];
        category.__hover_handler = () => this.handleCategoryHover(category);
        category.__out_handler = () => this.handleCategoryOut(category);
        category.__click_handler = () => this.handleCategoryClick(category);

        category.addEventListener('mouseover', category.__hover_handler);
        category.addEventListener('mouseout', category.__out_handler, false);
        category.addEventListener('click', category.__click_handler);
      }
    }
    removeListenersToCategoryLinks() {
      for (let i = 0; i < this.categories.length; i++) {
        const category = this.categories[i];
        category.removeEventListener('mouseover', category.__hover_handler);
        category.removeEventListener('mouseout', category.__out_handler, false);
        category.removeEventListener('click', category.__click_handler);
      }
    }

    handleCategoryHover(category) {
      const category_id = category.dataset.forCategoryId;
      if (this.active_category_id === category_id
          || this.hovering_over_category_id === category_id) {
        return;
      }

      this.hovering_over_category_id = category_id;
      clearTimeout(this.category_hover_timeout);

      this.category_hover_timeout = setTimeout(
          () => this.activateMenu(category_id),
          MegaMenuView.HOVER_CATEGORY_TIMEOUT_MS);
    }
    handleCategoryOut(category) {
      const category_id = category.dataset.forCategoryId;
      if (this.hovering_over_category_id === category_id) {
        this.hovering_over_category_id = null;
        clearTimeout(this.category_hover_timeout);
      }
    }

    handleCategoryClick(category) {
      const category_id = category.dataset.forCategoryId;
      if (this.active_category_id === category_id) {
        return;
      }

      clearTimeout(this.category_hover_timeout);
      this.category_hover_timeout = null;
      this.activateMenu(category_id);
    }

    activateMenu(category_id) {
      this.active_category_id = category_id;
      this.hovering_over_category_id = category_id;
      clearTimeout(this.category_hover_timeout);

      this.activateElementIfEq(this.categories, 'forCategoryId', category_id);
      this.activateElementIfEq(this.menus, 'categoryId', category_id, true);
    }

    activateElementIfEq(elements, dataset_key, value, reset_scroll_top = false) {
      for (let i = 0; i < elements.length; i++) {
        const element = elements[i];
        if (element.dataset[dataset_key] !== value) {
          element.classList.remove('is-active');
          continue;
        }

        element.classList.add('is-active');
        if (reset_scroll_top) {
          element.scrollTop = 0;
        }
      }
    }
  }

  function main() {
    const mega_menus = document.querySelectorAll('.js-mega-menu');
    for (let i = 0; i < mega_menus.length; i++) {
      new MegaMenu(mega_menus[i]);
    }
  }

  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    main();
  } else {
    document.addEventListener('DOMContentLoaded', main);
  }
})();
